/*
This software is Copyright ©️ 2020 The University of Southern California. All Rights Reserved. 
Permission to use, copy, modify, and distribute this software and its documentation for educational, research and non-profit purposes, without fee, and without a written agreement is hereby granted, provided that the above copyright notice and subject to the full license file found in the root of this software deliverable. Permission to make commercial use of this software may be obtained by contacting:  USC Stevens Center for Innovation University of Southern California 1150 S. Olive Street, Suite 2300, Los Angeles, CA 90115, USA Email: accounting@stevens.usc.edu

The full terms of this copyright and license should always be found in the root directory of this software deliverable as "license.txt" and if these terms are not found with this software, please contact the USC Stevens Center for the full license.
*/

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { fetchUsers, updateUserPermissions } from "../../../api";
import { RootState } from "../../store";
import { LoadStatus, User } from "../../../types";

export interface UsersState {
  users: Record<string, User>;
  userLoadStatus: LoadStatus;
}

const initialState: UsersState = {
  users: {},
  userLoadStatus: LoadStatus.NONE,
};

/** Actions */

export const loadUsers = createAsyncThunk(
  "users/loadUsers",
  async (_, thunkAPI): Promise<User[]> => {
    const state = thunkAPI.getState() as RootState;
    if (!state.login.user || !state.login.accessToken) {
      throw new Error("login required");
    }
    const accessToken = state.login.accessToken;
    return await fetchUsers(accessToken);
  }
);

export const updateUser = createAsyncThunk(
  "users/updateUser",
  async (data: User, thunkAPI): Promise<User> => {
    if (!data.id) throw new Error("id required");
    const state = thunkAPI.getState() as RootState;
    if (!state.login.user || !state.login.accessToken) {
      throw new Error("login required");
    }
    return await updateUserPermissions(
      state.login.accessToken,
      data.id,
      data.userRole
    );
  }
);

export const usersSlice = createSlice({
  name: "users",
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // load users
      .addCase(loadUsers.pending, (state) => {
        state.userLoadStatus = LoadStatus.IN_PROGRESS;
      })
      .addCase(loadUsers.fulfilled, (state, action) => {
        for (const user of action.payload) {
          state.users[user.id] = user;
        }
        state.userLoadStatus = LoadStatus.DONE;
      })
      .addCase(loadUsers.rejected, (state) => {
        state.userLoadStatus = LoadStatus.FAILED;
      })
      // update user
      .addCase(updateUser.fulfilled, (state, action) => {
        const user = action.payload;
        state.users[user.id] = user;
      });
  },
});

export default usersSlice.reducer;
